import { ChevronDown, ChevronRight, CirclePlay, Compass } from 'lucide-react'
import { Collapsible } from '@shopify/polaris'
import { useCallback, useRef, useState } from 'react'
import type { InvoiceVirtualTour } from '@features/virtual-tour'
import styles from './HelpLauncher.module.scss'

type VirtualToursMenuGroupProps = {
  onSelectTour: (slug: string) => void
  tours: InvoiceVirtualTour[]
  onMenuExpanded?: (tourCount: number, tourSlugs: string[]) => void
}

export const VirtualToursMenuGroup = ({
  onSelectTour,
  tours,
  onMenuExpanded,
}: VirtualToursMenuGroupProps) => {
  const [expanded, setExpanded] = useState(false)
  const hasTrackedExpansion = useRef(false)

  const toggleExpanded = useCallback(() => {
    setExpanded((previous) => {
      const next = !previous
      if (next && !hasTrackedExpansion.current) {
        hasTrackedExpansion.current = true
        onMenuExpanded?.(
          tours.length,
          tours.map((tour) => tour.slug),
        )
      }
      return next
    })
  }, [onMenuExpanded, tours])

  if (!tours.length) {
    return null
  }

  return (
    <div className={styles.virtualToursGroup}>
      <button
        type="button"
        className={styles.virtualToursGroupHeader}
        onClick={toggleExpanded}
        aria-expanded={expanded}
        aria-controls="help-launcher-virtual-tours"
      >
        <span className={styles.virtualToursGroupLabel}>
          <Compass size={18} strokeWidth={1.9} />
          Virtual tours
        </span>
        {expanded ? (
          <ChevronDown size={16} strokeWidth={1.9} />
        ) : (
          <ChevronRight size={16} strokeWidth={1.9} />
        )}
      </button>

      <Collapsible
        open={expanded}
        id="help-launcher-virtual-tours"
        transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
      >
        <ul className={styles.virtualToursList}>
          {tours.map((tour) => (
            <li key={tour.id}>
              <button
                type="button"
                className={styles.virtualToursListItem}
                onClick={() => onSelectTour(tour.slug)}
              >
                <CirclePlay size={16} strokeWidth={1.9} aria-hidden />
                {tour.label}
              </button>
            </li>
          ))}
        </ul>
      </Collapsible>
    </div>
  )
}
